import React, { useEffect, useState, useContext } from 'react';
import { InsurancePlan, ModalTypes } from '../../utils/types';
import { Autocomplete, TextField, Grid, Link, Popper } from '@mui/material';
import { SearchContainer, MobileBreakPoint, LinkText } from '../../theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { IconContainer, FooterLinks, FooterLinksMobile } from './styles';
import { isMobile } from 'react-device-detect';
import AutoClaimsModal from '../Modals/AutoClaimsModal';
import WorkersCompModal from '../Modals/WorkersCompModal';
import TravelersInsuranceModal from '../Modals/TravelersInsuranceModal';
import MedicalGroupModal from '../Modals/MedicalGroupModal';
import NoMatch from './NoMatch';
import { DataContext } from '../../containers/InsuranceChecker';
import { useSearch } from './search';
import { SuggestionBox } from './SuggestionBox';
import { EventAction, trackMixpanelEvent } from '../../mixpanelAnalytics';

type AutocompleteOption = string;

type Props = {
    handlePlanSelect: (plan: InsurancePlan) => void;
    handleStartOver: () => void;
}

const InsurancePlansContainer = ({ handlePlanSelect, handleStartOver }:Props): JSX.Element => {
    const { plans, groups } = useContext(DataContext);

    // Keeping track of value to use in search algorithm.
    const [ value, setValue ] = useState('');
    const [ lastTrackedValue, setLastTrackedValue ] = useState('');
    const [ options, setOptions ] = useState<AutocompleteOption[]>([]);
    const [ focus, setFocus ] = useState(isMobile || window.innerWidth <= MobileBreakPoint);
    const [ open, setOpen ] = useState(false);
    const [ noMatch, setNoMatch ] = useState(false);
    const [ showSuggestionBox, setShowSuggestionBox ] = useState(false);
    const { handleSearch } = useSearch();

    useEffect(() => {
        const options = plans.map(p => p.name);
        setOptions(options);
    }, [ plans ]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (value && lastTrackedValue !== value) {
                setLastTrackedValue(value);
                const filtered = handleSearch(value);
                const suggested = filtered.length ? filtered[0] : 'No Match';
                trackMixpanelEvent(EventAction.QUERIED_HEALTH_PLAN, { term: value, matchCount: filtered.length, suggested });
            }
        }, 1000);
        return () => clearTimeout(timeout);
    }, [ value ]);

    const [ activeModal, setActiveModal ] = useState<ModalTypes | null>(null);

    const closeModal = () => {
        setActiveModal(null);
    };

    const handleInputChange = (event:React.SyntheticEvent, value:string):void => {
        setValue(value);
        const selected = plans.filter(p => p.name === value);
        if (selected.length) {
            handlePlanSelect(selected[0]);
        }
    };

    const handleFocus = () => {
        setOpen(true);
        setFocus(true);
    };

    const handleBlur = () => {
        setOpen(false);
        setFocus(false);
    };

    const startOver = () => {
        setNoMatch(false);
        handleStartOver();
    };

    // Implementing custom autocomplete filtering that leverages
    // fuse fuzzy searching library.
    const handleFilter = () => {
        const results = handleSearch(value);
        if (results.find(plan => plan.toLowerCase() === value.toLowerCase())) {
            // Don't show the suggestion box if there's an exact match.
            setShowSuggestionBox(false);
        } else {
            // Don't show the suggestion box if there's no search value or no results.
            if (!value || results.length === 0) {
                setShowSuggestionBox(false);
            } else {
            // Show the search box when there's a search value and some results to choose
            // from, but not an exact match.
                setShowSuggestionBox(true);
            }
        }
        return results;
    };

    return (
        <div>
            <SearchContainer>
                <Autocomplete
                    PopperComponent={(props) =>
                        <Popper {...props} open={open}>
                            {props.children as React.ReactNode}
                            {showSuggestionBox && <SuggestionBox/>}
                        </Popper>
                    }
                    filterOptions={handleFilter}
                    className='fadeIn'
                    disablePortal
                    id='insurance-plan'
                    options={options}
                    sx={{
                        borderRadius: focus ? '40px 40px 0px 0px' : '40px',
                        boxShadow: focus ? '0 0 12px 3px #ccc' : 'none',
                    }}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    open={open}
                    onInputChange={handleInputChange}
                    autoComplete={true}
                    autoHighlight={true}
                    popupIcon={<></>}
                    renderInput={(params) => <TextField {...params} placeholder='Enter your health plan (Anthem, Blue Shield, United..., etc.)'/>}
                    componentsProps={{ popper: { placement: 'bottom-start', sx: { height: '50%' } } }}
                    noOptionsText={<Link onClick={() => { setNoMatch(true); setOpen(false); setFocus(false); }}>I don't see my plan</Link>}
                />
                <IconContainer>
                    <FontAwesomeIcon icon={faSearch} color='#CCC' size='2x'/>
                </IconContainer>
            </SearchContainer>
            <NoMatch show={noMatch} handleStartOver={startOver}/>
            <FooterLinks>
                See here for&nbsp;
                <span style={LinkText} onClick={() => { setActiveModal(ModalTypes.AutoClaims); }}>Auto Claims</span>,&nbsp;
                <span style={LinkText} onClick={() => { setActiveModal(ModalTypes.WorkersComp); }}>Worker's Comp</span>,&nbsp;
                <span style={LinkText} onClick={() => { setActiveModal(ModalTypes.TravelersInsurance); }}>Traveler's Insurance</span>,&nbsp;
                and&nbsp;
                <span style={LinkText} onClick={() => { setActiveModal(ModalTypes.MedicalGroupIPA); }}>Medical Group/IPAs</span>.
            </FooterLinks>
            <FooterLinksMobile>
                <Grid container>
                    <Grid item xs={6}>
                        See here for:
                    </Grid>
                    <Grid item xs={6}>
                        <Grid container>
                            <Grid item xs={12} style={LinkText} onClick={() => { setActiveModal(ModalTypes.AutoClaims); }}>Auto Claims</Grid>
                            <Grid item xs={12} style={LinkText} onClick={() => { setActiveModal(ModalTypes.WorkersComp); }}>Worker's Comp</Grid>
                            <Grid item xs={12} style={LinkText} onClick={() => { setActiveModal(ModalTypes.TravelersInsurance); }}>Traveler's Insurance</Grid>
                            <Grid item xs={12} style={LinkText} onClick={() => { setActiveModal(ModalTypes.MedicalGroupIPA); }}>Medical Group/IPAs</Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </FooterLinksMobile>
            <AutoClaimsModal open={activeModal === ModalTypes.AutoClaims} handleClose={ closeModal }/>
            <WorkersCompModal open={activeModal === ModalTypes.WorkersComp} handleClose={ closeModal }/>
            <TravelersInsuranceModal open={activeModal === ModalTypes.TravelersInsurance} handleClose={ closeModal }/>
            <MedicalGroupModal open={activeModal === ModalTypes.MedicalGroupIPA} handleClose={ closeModal } groups={groups}/>
        </div>
    );
};

export default InsurancePlansContainer;
